import api from './client';
import type { Ordre, OrdreIntervention } from 'types/ordre';
import type { ListParams } from './types';
import type { CarteGriseValidationResult } from './tracking';
import type { ContratFields } from 'types/contrat_location';

/** A pending "à prévoir" line surfaced for re-proposal at the next RDV. */
export interface PendingAPrevoir {
  ordre_id: string;
  ordre_num?: string;
  created_at?: { seconds: number; nanoseconds?: number };
  intervention: OrdreIntervention;
}

/** One create / move / delete of an ordre's reception or restitution RDV (back: rdv_move_log). */
export interface RdvMoveSlot {
  date: string;
  start_hour: number;
  category?: string | null;
}
export interface RdvMove {
  id: string;
  type?: 'reception' | 'restitution' | string;
  action: 'create' | 'move' | 'delete';
  from?: RdvMoveSlot | null;
  to?: RdvMoveSlot | null;
  source: string;
  source_label?: string;
  user_name?: string | null;
  note?: string | null;
  at: string;
}

export const ordresApi = {
  list: async (params?: ListParams & { org_id?: string; status?: string; assigned_to?: string; dossier_id?: string; has_or_number?: boolean; rated?: boolean; date_start?: number; date_end?: number; date_field?: string; offset?: number; sort_by?: string; sort_dir?: string }): Promise<{ items: Ordre[]; total: number }> => {
    const resp = await api.get('/ordres', { params });
    return { items: resp.data, total: parseInt(resp.headers['x-total-count'] || '0', 10) };
  },

  get: async (id: string): Promise<Ordre> => {
    const { data } = await api.get(`/ordres/${id}`);
    return data;
  },

  /** Who created / moved / deleted this ordre's RDV, newest first. */
  rdvMoves: async (id: string): Promise<RdvMove[]> => {
    const { data } = await api.get(`/ordres/${id}/rdv-moves`);
    return data;
  },

  dashboardStats: async (orgId: string, forceRefresh = false): Promise<{
    total: number;
    by_status: Record<string, number>;
    prepa: number;
    en_cours: number;
    termines: number;
    restitues: number;
    bloques: number;
    urgents: number;
    cached: boolean;
    stale: boolean;
    computed_at: string;
  }> => {
    const { data } = await api.post('/ordres/dashboard-stats', { org_id: orgId, force_refresh: forceRefresh });
    return data;
  },

  batch: async (ids: string[]): Promise<Ordre[]> => {
    if (ids.length === 0) return [];
    const { data } = await api.post('/ordres/batch', { ids });
    return data;
  },

  create: async (ordre: Omit<Ordre, 'id'> & { force?: boolean }): Promise<Ordre> => {
    const { data } = await api.post('/ordres', ordre);
    return data;
  },

  update: async (id: string, updates: Partial<Ordre> & { force?: boolean }): Promise<Ordre> => {
    const { data } = await api.patch(`/ordres/${id}`, updates);
    return data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/ordres/${id}`);
  },

  uploadDocument: async (ordreId: string, file: File, name?: string, documentType?: string): Promise<Ordre> => {
    const formData = new FormData();
    formData.append('file', file);
    if (name) formData.append('name', name);
    if (documentType) formData.append('document_type', documentType);
    const { data } = await api.post(`/ordres/${ordreId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  /** Staff drops the client's carte grise (internal booking flow). Files it on the
   *  dossier for the ordre's vehicle, exactly like the client's tracking upload. */
  /** Staff carte grise check — same AI as the client tracking page, run before the
   *  ordre exists (internal booking), so the plate comes from the form. */
  validateCarteGrise: async (file: File, plate?: string): Promise<CarteGriseValidationResult> => {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post('/ordres/validate-carte-grise', formData, {
      params: plate ? { plate } : undefined,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  uploadCarteGrise: async (ordreId: string, file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    await api.post(`/ordres/${ordreId}/carte-grise`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  importDocumentFromGcs: async (ordreId: string, sourceGcsPath: string, name?: string, documentType?: string): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/documents/import-from-gcs`, {
      source_gcs_path: sourceGcsPath,
      name,
      document_type: documentType,
    });
    return data;
  },

  getDocumentUrl: async (ordreId: string, documentId: string): Promise<string> => {
    const { data } = await api.get(`/ordres/${ordreId}/documents/${documentId}/url`);
    return data.url;
  },

  deleteDocument: async (ordreId: string, documentId: string): Promise<Ordre> => {
    const { data } = await api.delete(`/ordres/${ordreId}/documents/${documentId}`);
    return data;
  },

  addVehicleBookingEvent: async (ordreId: string, payload: { vehicle_plate: string; vehicle_name: string; start_date: string; end_date: string }): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/vehicle-booking-event`, payload);
    return data;
  },

  markGroupReady: async (ordreId: string, group: string): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/groups/${group}/mark-ready`);
    return data;
  },

  transferToAtelier: async (ordreId: string, group: string, charges: { specialty: string; intervention_ids: string[]; hours: number }[]): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/groups/${group}/transfer-atelier`, { charges });
    return data;
  },

  sendSignRequest: async (ordreId: string, documentId: string, signerName: string, signerEmail: string, signerPhone?: string): Promise<{ success: boolean; token: string; signing_url: string }> => {
    const { data } = await api.post(`/ordres/${ordreId}/documents/${documentId}/sign-request`, {
      signer_name: signerName,
      signer_email: signerEmail,
      signer_phone: signerPhone,
      document_id: documentId,
    });
    return data;
  },

  notifyRestitutionChange: async (ordreId: string, payload: {
    old_date_seconds?: number;
    new_date_seconds: number;
    comment?: string;
    notify?: boolean;
  }): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/notify-restitution-change`, payload);
    return data;
  },

  notifyReceptionChange: async (ordreId: string, payload: {
    old_date_seconds?: number;
    new_date_seconds: number;
    comment?: string;
    notify?: boolean;
  }): Promise<Ordre> => {
    const { data } = await api.post(`/ordres/${ordreId}/notify-reception-change`, payload);
    return data;
  },

  /** Pre-filled editable fields of the contrat de location/prêt (same defaults the PDF uses). */
  getContratLocationFields: async (ordreId: string): Promise<{
    fields: ContratFields;
    usage: "location" | "pret";
    conditions_url: string;
    conditions_qr: string;
    org: Record<string, string>;
  }> => {
    const { data } = await api.get(`/ordres/${ordreId}/contrat-location-fields`);
    return data;
  },

  generateContratLocation: async (
    ordreId: string,
    fields?: ContratFields,
  ): Promise<{
    documents: { id: string; name: string; type: string; gcs_path: string; url: string }[];
    fields: ContratFields;
  }> => {
    const { data } = await api.post(`/ordres/${ordreId}/generate-contrat-location`, { fields });
    return data;
  },

  /** Pending "à prévoir" interventions for a dossier (optionally one VIN) to re-propose at the next RDV. */
  getPendingAPrevoir: async (params: { dossier_id: string; vin?: string; exclude_ordre_id?: string }): Promise<PendingAPrevoir[]> => {
    const { data } = await api.get('/ordres/pending-a-prevoir', { params });
    return data;
  },

  /** Mark source à-prévoir lines as consumed by a newly created ordre (kept as history, removed from the pool). */
  consumeAPrevoir: async (newOrdreId: string, items: { ordre_id: string; intervention_id: string }[]): Promise<{ consumed: number }> => {
    const { data } = await api.post('/ordres/consume-a-prevoir', { new_ordre_id: newOrdreId, items });
    return data;
  },
};
